﻿import { createUrlSlug, isValidUrlSlug, urlSlugMatchesValue } from './slug';

const featuredImageStateKey = 'digitalJEdwinFeaturedImage';
const featuredImagePathPrefix = '/photo/';

export type FeaturedImageHistoryState = {
    [featuredImageStateKey]: {
        imageKey: string;
    };
};

export function createFeaturedImageHistoryState(imageKey: string): FeaturedImageHistoryState {
    if (imageKey.length === 0) throw new RangeError('Featured image key cannot be empty.');

    return {
        [featuredImageStateKey]: { imageKey }
    };
}

export function getFeaturedImageHistoryKey(state: unknown): string | null {
    if (state == null || typeof state !== 'object') return null;

    const featuredImageState = (state as Record<string, unknown>)[featuredImageStateKey];
    if (featuredImageState == null || typeof featuredImageState !== 'object') return null;

    const imageKey = (featuredImageState as Record<string, unknown>).imageKey;
    return typeof imageKey === 'string' && imageKey.length > 0 ? imageKey : null;
}

export function createFeaturedImagePath(title: string): string {
    return `${featuredImagePathPrefix}${createFeaturedImageSlug(title)}`;
}

export function createFeaturedImageSlug(title: string): string {
    if (title.trim().length === 0) throw new RangeError('Featured image title cannot be empty.');

    const slug = createUrlSlug(title);
    if (slug.length === 0) throw new RangeError('Featured image title must contain a URL-friendly character.');
    return slug;
}

export function featuredImageSlugMatchesTitle(slug: string, title: string): boolean {
    return urlSlugMatchesValue(slug, title);
}

export function getFeaturedImageSlugFromPath(pathname: string): string | null {
    if (!pathname.toLowerCase().startsWith(featuredImagePathPrefix)) return null;

    const slug = pathname.slice(featuredImagePathPrefix.length).replace(/\/+$/, '');
    return isValidUrlSlug(slug) ? slug : null;
}

export function isBackForwardPageShow(persisted: boolean, navigationType: string | undefined): boolean {
    return persisted || navigationType === 'back_forward';
}

const collectionPathReservedSegments = new Set([
    'about',
    'admin',
    'api',
    'contact',
    'favicon.ico',
    'healthz',
    'hubs',
    'index',
    'merchant-feed.xml',
    'order',
    'photo',
    'prints',
    'q',
    'return-policy',
    'rights',
    'robots.txt',
    'shop',
    'subscribe',
    'sitemap.xml'
]);

export type CollectionSlugCandidate = {
    name: string;
    slug: string;
};

/**
 * Extracts the collection slug from a single-segment pathname such as /Evening-Light.
 *
 * Reserved segments that belong to real routes, SignalR hubs, and static assets are rejected, and multi-segment
 * paths (for example /photo/Sunset) are never treated as collection links.
 *
 * @param pathname - The pathname of the current location.
 * @returns The collection slug, or null when the path is not a collection link.
 */
export function getCollectionSlugFromPath(pathname: string): string | null {
    const segment = pathname.replace(/\/+$/, '').slice(1);
    if (segment.length === 0 || segment.includes('/')) return null;
    if (collectionPathReservedSegments.has(segment.toLowerCase())) return null;
    return isValidUrlSlug(segment) ? segment : null;
}

/**
 * Resolves a requested collection slug against the public collection list with the same precedence as the server:
 * an administrator-defined slug first, then the name-derived slug from the shared encoding rules.
 *
 * @param candidates - The public collections as name/slug records.
 * @param slug - The slug taken from the request path.
 * @returns The stored slug of the matching collection, or null when nothing matches.
 */
export function findCollectionSlug(candidates: readonly CollectionSlugCandidate[], slug: string): string | null {
    let storedSlugMatch: string | null = null;
    let nameSlugMatch: string | null = null;
    for (const candidate of candidates) {
        if (storedSlugMatch == null && candidate.slug.toLowerCase() === slug.toLowerCase()) storedSlugMatch = candidate.slug;
        if (nameSlugMatch == null && urlSlugMatchesValue(slug, candidate.name)) nameSlugMatch = candidate.slug;
    }

    return storedSlugMatch ?? nameSlugMatch;
}
